import { onBeforeUnmount, reactive, ref } from 'vue'
import { DocumentPreview, useModal } from '@staff/ui'

export interface PreviewFile {
  blob: Blob
  contentType: string
  fileName?: string
}

export interface OpenFileOptions {
  id: string
  title: string
  load: () => Promise<PreviewFile>
  fallbackContentType?: string
  fallbackFileName?: string
}

export const useFilePreview = () => {
  const { openModal, closeModal, component } = useModal()
  const selectedId = ref<string | null>(null)
  const openingId = ref<string | null>(null)
  const previewProps = reactive({
    src: '',
    contentType: '',
    fileName: '',
    isLoading: false,
    error: '',
  })

  const clearPreviewContent = () => {
    if (previewProps.src) {
      URL.revokeObjectURL(previewProps.src)
    }

    previewProps.src = ''
    previewProps.contentType = ''
    previewProps.fileName = ''
    previewProps.error = ''
  }

  const closePreview = () => {
    selectedId.value = null
    previewProps.isLoading = false
    openingId.value = null
    clearPreviewContent()
  }

  const openFile = async (options: OpenFileOptions) => {
    if (openingId.value) return

    const requestedId = options.id
    selectedId.value = requestedId
    clearPreviewContent()
    previewProps.isLoading = true
    openingId.value = requestedId

    openModal(options.title, DocumentPreview, previewProps, {
      size: 'large',
      onClose: closePreview,
    })

    try {
      const { blob, contentType, fileName } = await options.load()

      const url = URL.createObjectURL(blob)
      if (selectedId.value !== requestedId) {
        URL.revokeObjectURL(url)
        return
      }

      previewProps.src = url
      previewProps.contentType = contentType || options.fallbackContentType || ''
      previewProps.fileName = fileName ?? options.fallbackFileName ?? requestedId
    } catch (err) {
      if (selectedId.value !== requestedId) return

      previewProps.error = err instanceof Error && err.message ? err.message : 'Kunde inte hämta filen.'
    } finally {
      if (selectedId.value === requestedId) {
        previewProps.isLoading = false
        openingId.value = null
      }
    }
  }

  onBeforeUnmount(() => {
    if (component.value === DocumentPreview) {
      closeModal()
      return
    }

    closePreview()
  })

  return {
    openingId,
    openFile,
  }
}
